// shared/ui/ImagenesDetalleSection.jsx
// Orquestación de imágenes del detalle (capacitaciones y visitas): modo eliminar (marcas sin API, Esc/Cancelar), diálogo de
// confirmación, aplicar eliminación con fade, agregar imágenes con avisos Swal y advertencia al salir con marcas pendientes.
// `imagenes`: lista del view model (data.imagenes); `api`: lo que devuelve useImagenesDetalle (vía el hook del detalle).
import React from 'react';
import ImagenesDetalleCard from './ImagenesDetalleCard.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import AdvertenciaSalida from './AdvertenciaSalida.jsx';
import { FADE_MS } from './ImagenGuardada.jsx';
import { showAviso, showError, showSuccess } from './feedback';
import {
  mensajesAgregarImagenes,
  soloExcedeLimite,
  mensajeEliminacionFallida,
  descripcionSalidaConMarcas,
  descripcionSalidaConSubidas,
  mensajeSubidasFallidas,
  textoImagenesEliminadas,
} from '../domain/media';

const ImagenesDetalleSection = ({ imagenes, subtitle, alt, api }) => {
  const {
    agregarImagenes,
    totalImagenes,
    limiteAlcanzado,
    pendientes,
    subiendoImagenes,
    reintentar,
    quitarPendiente,
    confirmarSubida,
    eliminando,
    porEliminar,
    marcarParaEliminar,
    deshacerEliminar,
    descartarMarcas,
    eliminarMarcadas,
    quitarImagenes,
  } = api;

  // Modo eliminar: las imágenes se MARCAN (sin API) y se eliminan al presionar «Eliminar». Sale con Cancelar, Esc
  // o (derivado) cuando no quedan imágenes.
  const [modoActivo, setModoActivo] = React.useState(false);
  const [aEliminar, setAEliminar] = React.useState(null);
  const [saliendo, setSaliendo] = React.useState({});
  const timers = React.useRef([]);
  const enCurso = pendientes.filter(p => p.estado === 'procesando' || p.estado === 'queued' || p.estado === 'uploading').length;
  const modoEliminar = modoActivo && imagenes.length > 0;
  const dialogoAbierto = aEliminar !== null;

  React.useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const cancelarModo = React.useCallback(() => {
    descartarMarcas();
    setModoActivo(false);
  }, [descartarMarcas]);

  React.useEffect(() => {
    if (!modoEliminar || dialogoAbierto || eliminando) return undefined;
    const onKeyDown = event => event.key === 'Escape' && cancelarModo();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [modoEliminar, dialogoAbierto, eliminando, cancelarModo]);

  const confirmarMarcar = () => {
    marcarParaEliminar(aEliminar);
    setAEliminar(null);
  };

  const esperar = ms =>
    new Promise(resolve => {
      timers.current.push(setTimeout(resolve, ms));
    });

  const aplicarEliminacion = async () => {
    if (!porEliminar.length) {
      setModoActivo(false);
      return;
    }
    const { eliminadas, fallidas } = await eliminarMarcadas();
    if (eliminadas.length) {
      const reducida = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setSaliendo(Object.fromEntries(eliminadas.map(mediaUuid => [mediaUuid, true])));
      await esperar(reducida ? 0 : FADE_MS);
      quitarImagenes(eliminadas);
      setSaliendo({});
    }
    if (fallidas.length) {
      await showError({ message: mensajeEliminacionFallida(fallidas.length) });
      return;
    }
    setModoActivo(false);
    showSuccess(textoImagenesEliminadas(eliminadas.length));
  };

  const handleAddImages = async files => {
    setModoActivo(false);
    try {
      const resultado = await agregarImagenes(files);
      const mensajes = mensajesAgregarImagenes(resultado);
      if (mensajes.length) await (soloExcedeLimite(resultado) ? showAviso : showError)({ messages: mensajes });
      if (resultado.fallidas > 0) await showError({ message: mensajeSubidasFallidas(resultado.fallidas) });
    } catch (e) {
      await showError(e);
    }
  };

  return (
    <>
      <ImagenesDetalleCard
        imagenes={imagenes}
        subtitle={subtitle}
        alt={alt}
        totalImagenes={totalImagenes}
        pendientes={pendientes}
        subiendoImagenes={subiendoImagenes}
        limiteAlcanzado={limiteAlcanzado}
        modoEliminar={modoEliminar}
        eliminando={eliminando}
        saliendo={saliendo}
        onAddImages={handleAddImages}
        onEntrarModoEliminar={() => setModoActivo(true)}
        onCancelarModoEliminar={cancelarModo}
        onAplicarEliminacion={aplicarEliminacion}
        onEliminarImagen={setAEliminar}
        onDeshacerEliminar={deshacerEliminar}
        onQuitarPendiente={quitarPendiente}
        onReintentarPendiente={reintentar}
        onPendienteListo={confirmarSubida}
      />
      <ConfirmDialog
        open={dialogoAbierto}
        title="¿Confirmas que quieres eliminar esta imagen?"
        description="La imagen se marcará para eliminarse al presionar «Eliminar». Podrás deshacerlo antes."
        onConfirm={confirmarMarcar}
        onCancel={() => setAEliminar(null)}
      />
      {/* Un solo bloqueo (history admite uno): las subidas en curso tienen prioridad sobre las marcas. */}
      <AdvertenciaSalida
        cuando={subiendoImagenes || porEliminar.length > 0}
        descripcion={subiendoImagenes ? descripcionSalidaConSubidas(enCurso) : descripcionSalidaConMarcas(porEliminar.length)}
        confirmText={subiendoImagenes ? 'Salir' : undefined}
      />
    </>
  );
};

export default ImagenesDetalleSection;
